/**
 * HeroCreatorIntro — pierwsze 10 sekund w Kreatorze strony.
 *
 * 3 dymki onboarding pokazujące co user ma zrobić:
 *   1. Wybierz styl  (templates strip)
 *   2. Dodaj zdjęcie (image upload)
 *   3. Sprawdź podgląd (live preview)
 *
 * One-time: localStorage `apisai_hero_intro_seen_v1` blokuje ponowne wyświetlanie.
 * Animacja: stagger entrance, dismiss on click/CTA, smooth fade-out.
 */
import { useEffect, useState } from 'react';
import { X, Sparkles, ImageIcon, Eye, ArrowRight } from 'lucide-react';

const LS_KEY = 'apisai_hero_intro_seen_v1';

export default function HeroCreatorIntro() {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(LS_KEY);
      if (!seen) {
        // Małe opóźnienie żeby layout się ustabilizował przed pojawieniem się dymka
        const t = setTimeout(() => setOpen(true), 400);
        return () => clearTimeout(t);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const close = () => {
    setClosing(true);
    setTimeout(() => {
      try { localStorage.setItem(LS_KEY, '1'); } catch { /* ignore */ }
      setOpen(false);
    }, 220);
  };

  if (!open) return null;

  const tips = [
    { icon: Sparkles,  title: 'Wybierz styl', sub: 'Gotowe szablony u góry — kliknij ten, który pasuje do Twojej marki.' },
    { icon: ImageIcon, title: 'Dodaj zdjęcie', sub: 'Twoje zdjęcie + logo. Dobre zdjęcie robi 80% wrażenia.' },
    { icon: Eye,       title: 'Zobacz podgląd', sub: 'Środkowa kolumna pokazuje na żywo jak strona wygląda. Przełącz Desktop ↔ Mobile.' },
  ];

  return (
    <>
      {/* Soft backdrop — nie blokujący, tylko subtelnie ściemniający */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${closing ? 'opacity-0' : 'opacity-100'}`}
        onClick={close}
        aria-hidden="true"
        data-testid="hero-intro-backdrop"
      />

      {/* Floating card bottom-right (desktop) / centered (mobile) */}
      <div
        className={[
          'fixed z-50 right-5 bottom-5 sm:right-7 sm:bottom-7 left-5 sm:left-auto max-w-md',
          'transition-all duration-300',
          closing ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0',
        ].join(' ')}
        data-testid="hero-creator-intro"
      >
        <div
          className="relative rounded-2xl p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] ring-1 ring-white/8"
          style={{ background: 'linear-gradient(160deg, #0F172A 0%, #172033 100%)' }}
        >
          {/* Orange glow */}
          <div
            className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-50"
            style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.55), transparent 65%)' }}
            aria-hidden="true"
          />

          <button
            onClick={close}
            data-testid="hero-intro-dismiss"
            className="absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center text-white/45 hover:text-white hover:bg-white/8 transition-colors"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="relative space-y-5">
            <div className="space-y-1.5">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--apisai-primary)]">
                Krok 1 z 4
              </p>
              <h2 className="text-white text-xl font-bold tracking-tight">
                Witaj w Kreatorze strony
              </h2>
              <p className="text-white/65 text-sm leading-relaxed">
                3 rzeczy, które tu zrobisz:
              </p>
            </div>

            <ol className="space-y-3">
              {tips.map((tip, i) => {
                const TipIcon = tip.icon;
                return (
                  <li
                    key={i}
                    className="flex items-start gap-3 apisai-rise"
                    style={{ animationDelay: `${0.15 + i * 0.11}s` }}
                    data-testid={`hero-intro-tip-${i + 1}`}
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-white/8 ring-1 ring-white/10 flex items-center justify-center">
                      <TipIcon className="w-4 h-4 text-[var(--apisai-primary)]" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-white text-sm font-semibold leading-tight">
                        {i + 1}. {tip.title}
                      </p>
                      <p className="text-white/55 text-xs mt-1 leading-relaxed">
                        {tip.sub}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={close}
                data-testid="hero-intro-start"
                className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-all shadow-[0_12px_30px_-8px_rgba(249,115,22,0.6)]"
              >
                Zaczynamy
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={close}
                className="text-white/55 hover:text-white text-xs font-medium transition-colors"
              >
                Już to znam
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
